import { computed, inject, Injectable, signal } from '@angular/core';

import type { SearchSuggestion } from '@mercadoni/stark-ui';

import { toolPathSegment } from '../../../shared/utils/tool-path.util';
import { toStarkIcon } from '../../config';
import type { SearchResultItem } from '../../interfaces/navigation.interface';
import { FavoritesService } from '../firestore/favorites.service';
import { ToolRegistryService } from '../firestore/tool-registry.service';

@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  private readonly toolRegistry = inject(ToolRegistryService);
  private readonly favorites = inject(FavoritesService);

  readonly isOpen = signal(false);
  readonly query = signal('');
  readonly activeTab = signal('all');
  /** Set when opened from a sidebar category click — scopes results to that category. */
  readonly categoryFilter = signal<string | null>(null);

  readonly tabs = [
    { id: 'all', label: 'Todo' },
    { id: 'tools', label: 'Herramientas' },
  ];

  readonly items = computed<SearchResultItem[]>(() => {
    // Build from live Firestore data — includes all categories + their tools
    const tools = this.toolRegistry.categories().flatMap(cat =>
      cat.tools.map(tool => ({
        id: `tool-${tool.id}`,
        sourceId: tool.id,
        label: tool.name,
        description: tool.description ?? '',
        // sk-search-modal renders through sk-icon, whose registry differs from Lucide's.
        icon: toStarkIcon(tool.icon ?? tool.icon_url),
        route: `/${cat.id}/${toolPathSegment(tool)}`,
        type: 'tools' as const,
        typeLabel: 'Herramienta',
        favorite: this.favorites.isFavorite(tool.id),
        categoryId: cat.id,
      }))
    );

    return tools;
  });

  readonly filteredItems = computed(() => {
    const query = this.query().trim().toLowerCase();
    const tab = this.activeTab();
    const categoryId = this.categoryFilter();

    return this.items().filter(item => {
      const matchesCategory = !categoryId || item.categoryId === categoryId;
      const matchesTab = tab === 'all' || item.type === tab;
      const matchesQuery =
        !query ||
        item.label.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query);

      return matchesCategory && matchesTab && matchesQuery;
    });
  });

  readonly suggestions = computed<SearchSuggestion[]>(() =>
    this.filteredItems().slice(0, 6).map(item => ({
      label: item.label,
      value: item.id,
      description: item.description,
      icon: item.icon,
    }))
  );

  open(): void {
    this.categoryFilter.set(null);
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
    this.query.set('');
    this.categoryFilter.set(null);
  }

  /** Opens scoped to one category — the sidebar's "browse this category" entry point. */
  openFromCategory(categoryId: string): void {
    this.categoryFilter.set(categoryId);
    this.activeTab.set('tools');
    this.query.set('');
    this.isOpen.set(true);
  }

  setQuery(query: string): void {
    this.query.set(query);
  }

  setActiveTab(tab: string): void {
    this.activeTab.set(tab);
  }

  getItemBySuggestion(suggestion: SearchSuggestion): SearchResultItem | undefined {
    return this.items().find(result => result.id === suggestion.value);
  }
}
